/**
 * paper.js —— 一页纸表格 · 全站脚本（主题 + 返回顶部 + 文档引擎）
 * ---------------------------------------------------------------------------
 * ① 主题切换（页头 #pp-theme；键 tb-theme 与工具站/台账/转念共享，一处切换全站生效）
 * ② 返回顶部（滚动超一屏出现）
 * ③ 文档引擎（[data-pp-doc]）：按 data js（window.PP_DOCS[slug]）渲染字段与行表格
 *    → 引擎计算产出纸面 blocks → ToolUI.paper 渲染 A4 纸面 → 打印。
 *    组件依赖（工具站共享产物）：ui.min.js 的 ToolUI.rowTable / ToolUI.paper，
 *    引擎在 tools/engine/<name>.js（直引，如 paperkit）——文档页 footer 按序挂载
 *    「ui.min.js → 引擎 js → data js → paper.min.js」。
 *
 * 纪律（同转念 decide.js）：结果区全 createElement/textContent（天然防注入）；
 * 同状态零 DOM 写入；引擎错误码 → data js 文案映射（引擎零文案）。
 *
 * 新增文档三件套（引擎零改动）：config/paper.php docs 注册 + data/<slug>.js +
 * doc/<slug>.html —— 见 docs/paper-site/README.md。
 */
(function (global) {
  'use strict';

  var doc = global.document;
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function el(tag, cls) {
    var node = doc.createElement(tag);
    if (cls) { node.className = cls; }
    return node;
  }
  function iconEl(name) {
    var svg = doc.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'tool-icon');
    svg.setAttribute('aria-hidden', 'true');
    var use = doc.createElementNS(SVG_NS, 'use');
    use.setAttribute('href', '#i-' + name);
    svg.appendChild(use);
    return svg;
  }

  /* 组标题（字段分组 pp-group-title / 行段 pp-rows-title）：文本 span + 进度徽章。
     文本必须显式 textContent——el() 只收两参，此前 el('p', cls, text) 的第三参
     被静默丢弃（组标题从未显示的潜伏 bug，第十五批修复，冒烟已锁文本） */
  function titleEl(cls, text, badge) {
    var p = el('p', cls);
    var t = el('span', 'pp-title-text');
    t.textContent = text;
    p.appendChild(t);
    if (badge) { p.appendChild(badge); }
    return p;
  }
  /* 进度徽章：信息性提示，绝不拦截生成——空值合法是本栏目文档哲学
     （三餐留白打印后手填/空分组归其他）。初始隐藏，首刷铺开；同状态零 DOM 写入 */
  function badgeEl() {
    var b = el('span', 'pp-progress');
    b.setAttribute('aria-hidden', 'true');
    b.hidden = true;
    return b;
  }
  function paintBadge(badge, done, total) {
    var isEmpty = total === 0;
    if (badge.hidden !== isEmpty) { badge.hidden = isEmpty; }
    if (isEmpty) { return; }
    var all = done >= total;
    var text = all ? '\u2713' : done + '/' + total;
    if (badge.textContent !== text) { badge.textContent = text; }
    var cls = all ? 'pp-progress is-done' : 'pp-progress';
    if (badge.className !== cls) { badge.className = cls; }
  }

  /* ---------------- ① 主题 ---------------- */
  function initTheme() {
    var btn = doc.getElementById('pp-theme');
    if (!btn) { return; }
    function label() {
      var dark = doc.documentElement.getAttribute('data-theme') === 'dark';
      btn.setAttribute('aria-label', dark ? '切换到浅色主题' : '切换到深色主题');
      btn.setAttribute('aria-pressed', dark ? 'true' : 'false');
    }
    label();
    btn.addEventListener('click', function () {
      var dark = doc.documentElement.getAttribute('data-theme') === 'dark';
      var next = dark ? 'light' : 'dark';
      doc.documentElement.setAttribute('data-theme', next);
      try { global.localStorage.setItem('tb-theme', next); } catch (e) { /* 隐私模式不持久化 */ }
      label();
    });
  }

  /* ---------------- ② 返回顶部（对齐 tools/CMS：悬浮按钮 + 滚动进度环） ---------------- */
  function initBackTop() {
    var wrap = el('div', 'tool-float');
    var btn = el('button', 'tool-float-btn');
    btn.type = 'button';
    btn.setAttribute('aria-label', '回到顶部');
    btn.setAttribute('title', '回到顶部');
    var ring = doc.createElementNS(SVG_NS, 'svg');
    ring.setAttribute('class', 'tool-float-ring');
    ring.setAttribute('viewBox', '0 0 48 48');
    ring.setAttribute('aria-hidden', 'true');
    var ringCls = ['tool-float-ring-bg', 'tool-float-ring-pg'];
    var pg = null;
    for (var i = 0; i < ringCls.length; i++) {
      var c = doc.createElementNS(SVG_NS, 'circle');
      c.setAttribute('class', ringCls[i]);
      c.setAttribute('cx', '24');
      c.setAttribute('cy', '24');
      c.setAttribute('r', '22');
      if (i === 1) { pg = c; }
      ring.appendChild(c);
    }
    btn.appendChild(ring);
    btn.appendChild(iconEl('arrow-up'));
    wrap.appendChild(btn);
    doc.body.appendChild(wrap);
    btn.addEventListener('click', function () {
      global.scrollTo({ top: 0, behavior: 'smooth' });
    });
    var threshold = 300;
    var CIRC = 138.2; // 2π×22，进度环周长（与 tools.css stroke-dasharray 一致）
    var lastPct = -1;
    function sync() {
      var top = global.pageYOffset || doc.documentElement.scrollTop || 0;
      var show = top > threshold;
      var max = doc.documentElement.scrollHeight - global.innerHeight;
      var pct = max > 0 ? Math.min(Math.max(top / max, 0), 1) : 0;
      var pctChanged = pct !== lastPct;
      if (pctChanged) { lastPct = pct; }
      btn.className = btn.className.replace(/\s*is-show/, '') + (show ? ' is-show' : '');
      if (pctChanged) { pg.style.strokeDashoffset = (CIRC * (1 - pct)).toFixed(1); }
    }
    global.addEventListener('scroll', sync, { passive: true });
    sync();
  }

  /* ---------------- ③ 文档引擎 ---------------- */

  /** 引擎错误 → 用户文案：行号属性优先（{row} 占位替换），其余按错误码映射；
   *  未知码退回 INVALID 文案。全 textContent 输出（防注入）。
   *  行号键（第十三批通用化）：显式四键在前保持既有优先级；其余 camelCase
   *  行键（如 petBoarding 的 mealRow/supplyRow/habitRow）按 MEAL_ROW 形态到
   *  data js errors 里找对应文案——未来任何新行段零渲染器改动。 */
  function engineErrMsg(e, cfg) {
    var errs = (cfg && cfg.errors) || {};
    var explicit = [['dayRow', 'DAY_ROW'], ['taskRow', 'TASK_ROW'], ['priceRow', 'PRICE_ROW'], ['itemRow', 'ITEM_ROW']];
    for (var i = 0; i < explicit.length; i++) {
      if (e && e[explicit[i][0]] != null && errs[explicit[i][1]]) {
        return String(errs[explicit[i][1]]).replace('{row}', String(e[explicit[i][0]]));
      }
    }
    if (e) {
      var keys = Object.keys(e);
      for (var j = 0; j < keys.length; j++) {
        var k = keys[j];
        if (e[k] == null || !/^[a-z][a-zA-Z]*Row$/.test(k)) { continue; }
        var snake = k.replace(/([A-Z])/g, '_$1').toUpperCase();
        if (errs[snake]) {
          return String(errs[snake]).replace('{row}', String(e[k]));
        }
      }
    }
    var code = String((e && e.message) || 'INVALID');
    return errs[code] || errs.INVALID || code;
  }

  /** 'YYYY-MM-DD' 本地今天（date 字段缺省值：用户视角「从今天开始」） */
  function todayStr() {
    var d = new Date();
    return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
  }

  /** 字段缺省值：显式 value 优先；date 无 value → 今天（首次渲染与重填共用，保证一致） */
  function fieldDefault(f) {
    if (!f) { return ''; }
    if (f.value != null && f.value !== '') { return String(f.value); }
    return f.type === 'date' ? todayStr() : '';
  }

  /** 步进器按钮（左 − / 右 +）：结构与工具站 partial 同构（aria-label 用符号） */
  function stepBtn(dir, forId) {
    var b = el('button', 'tool-step');
    b.type = 'button';
    b.setAttribute('data-step', dir);
    b.setAttribute('data-for', forId);
    b.setAttribute('aria-label', dir === 'up' ? '+' : '-');
    var arrow = el('span', 'tool-step-arrow');
    arrow.textContent = dir === 'up' ? '+' : '\u2212';
    b.appendChild(arrow);
    return b;
  }

  function mountDoc(root) {
    var slug = String(root.getAttribute('data-pp-doc') || '');
    var cfg = (global.PP_DOCS || {})[slug];
    var fieldsBox = root.querySelector('[data-pp-fields]');
    var rowsBox = root.querySelector('[data-pp-rows]');
    var errBox = root.querySelector('[data-pp-error]');
    var sumBox = root.querySelector('[data-pp-summary]');
    var genBtn = root.querySelector('[data-pp-generate]');
    var printBtn = root.querySelector('[data-pp-print]');
    var resetBtn = root.querySelector('[data-pp-reset]');
    var resultBox = global.document ? doc.querySelector('[data-pp-result]') : null;
    /* cfg / 引擎 / 组件任一缺失（data js 没就位、直引引擎脚本 404、ui.min.js 旧缓存）
       静默退出：页面保持「默认内容已排好」的空容器状态，不报错不占位。
       首页（hub）没有填写容器，mountDoc 天然不触发。 */
    var UI = global.ToolUI;
    var kit = global.ToolEngine;
    if (!cfg || !genBtn || !UI || !UI.paper || !UI.rowTable || !kit) { return; }
    /* cfg.engine 是 ToolEngine 下的点路径（如 'paperkit.choreSheet'）——
       引擎文件跨栏目共享命名空间，data js 只声明用哪个函数 */
    var engine = kit;
    String(cfg.engine || '').split('.').forEach(function (k) { engine = engine ? engine[k] : null; });
    if (!engine) { return; }

    /* —— 字段区（text / select / date 三型，labels 全部来自 data js；
          结果区全 createElement/textContent——select option 同样防注入） —— */
    var inputs = {};
    var hasStepper = false;
    var wrapEl = null;
    /* 进度徽章口径（第十五批）：字段组 = 组内字段全非空；行段 = 必填列全非空的行数
       （required 由 data js 声明、与引擎必填对齐；缺省全列，宁严勿松——徽章只会
       少报就绪、不会假绿） */
    var fieldGroups = [];
    var rowSegs = [];
    var curGroup = null;
    (cfg.fields || []).forEach(function (f) {
      /* 分组标题（f.groupTitle，第十三批）：字段多时分组呈现（宠物信息/寄养时间/紧急联系），
         插在组内第一个字段行之前；无该键的字段照旧平铺（向后兼容） */
      if (f.groupTitle) {
        curGroup = { badge: badgeEl(), items: [] };
        fieldGroups.push(curGroup);
        fieldsBox.appendChild(titleEl('pp-group-title', f.groupTitle, curGroup.badge));
      }
      var row = el('div', 'tool-field');
      wrapEl = null;
      /* el() 只收两参（titleEl 同坑，第十五批）：label 文本必须显式 textContent，
         否则字段标签整列空白（只有 aria-label/placeholder 兜底，第十七批修复） */
      var lab = el('label', 'tool-label');
      lab.textContent = f.label;
      lab.setAttribute('for', 'pp-f-' + f.name);
      row.appendChild(lab);
      var input;
      if (f.type === 'select') {
        input = doc.createElement('select');
        input.className = 'tool-input';
        input.id = 'pp-f-' + f.name;
        input.setAttribute('aria-label', f.label);
        (f.options || []).forEach(function (o) {
          var opt = doc.createElement('option');
          opt.value = o.value;
          opt.textContent = o.label;
          input.appendChild(opt);
        });
        input.value = fieldDefault(f);
      } else {
        input = doc.createElement('input');
        input.className = 'tool-input';
        input.type = 'text';
        input.id = 'pp-f-' + f.name;
        input.value = fieldDefault(f);
        input.autocomplete = 'off';
        if (f.type === 'date') { input.setAttribute('data-tool-datepicker', ''); }
        if (f.placeholder) { input.setAttribute('placeholder', f.placeholder); }
        input.setAttribute('aria-label', f.label);
        /* 数值字段步进器（f.stepper: {scale,min,max}）：左 − 右 +，结构同工具站 partial
           （data-step-* 写在 input 上，UI.stepper 在按钮上找不到时回读输入框） */
        if (f.stepper) {
          hasStepper = true;
          input.setAttribute('inputmode', 'decimal');
          if (f.stepper.scale != null) { input.setAttribute('data-step-scale', String(f.stepper.scale)); }
          if (f.stepper.min != null) { input.setAttribute('data-step-min', String(f.stepper.min)); }
          if (f.stepper.max != null) { input.setAttribute('data-step-max', String(f.stepper.max)); }
          wrapEl = el('span', 'tool-stepper');
          wrapEl.appendChild(stepBtn('down', input.id));
          wrapEl.appendChild(input);
          wrapEl.appendChild(stepBtn('up', input.id));
        }
      }
      if (wrapEl) { row.appendChild(wrapEl); } else { row.appendChild(input); }
      fieldsBox.appendChild(row);
      inputs[f.name] = input;
      if (curGroup) { curGroup.items.push(input); }
    });
    if (fieldsBox && fieldsBox.hasChildNodes()) { fieldsBox.removeAttribute('hidden'); }
    /* 日期字段挂日历组件（ui.min.js 共享产物，容器级初始化一次） */
    if (UI.datePicker) { UI.datePicker(fieldsBox); }
    /* 步进器接线（容器级委托，UI.stepper 按 data-for 定位输入框）：
       点步进只改值 + 清错误；纸面已生成时静默重算（keepView，不滚动抢视口） */
    if (hasStepper && UI.stepper) {
      UI.stepper(fieldsBox, function () {
        clearError();
        refreshBadges();
        if (resultBox && !resultBox.hasAttribute('hidden')) { generate(true); }
      });
    }

    /* —— 快捷示例（第十七批）：同台账「点一下自动填」契约——容器 .tool-examples +
          按钮 .tool-example、折叠 .tool-examples-more / .tool-example--extra
          （类名是共享样式，不能改；折叠不删减，隐藏按钮仍在 DOM）。
          paper 的默认内容已排好，示例给的是「另一套现成填法」：点一下整套替换
          （fields 缺键=复位默认、rows 缺键=复位 initial）并直接生成纸面——
          不会填的人一步看到成品。全 createElement/textContent（防注入，同本文件纪律） —— */
    var examplesBox = root.querySelector('[data-pp-examples]');
    var exList = cfg.examples || [];
    var exNote = null;
    if (examplesBox && exList.length) {
      examplesBox.hidden = false;
      var cap = el('span', 'tool-examples-label');
      cap.appendChild(iconEl('zap'));
      cap.appendChild(doc.createTextNode('点一下自动填'));
      examplesBox.appendChild(cap);
      exList.forEach(function (ex, i) {
        var b = el('button', 'tool-example');
        b.type = 'button';
        b.setAttribute('data-ex', String(i));
        b.textContent = ex.label || '示例 ' + (i + 1);
        examplesBox.appendChild(b);
      });
      var exLimit = parseInt(examplesBox.getAttribute('data-examples-visible') || '', 10);
      if (isNaN(exLimit) || exLimit <= 0) { exLimit = 6; }
      var syncExamples = function () {
        var expanded = examplesBox.classList.contains('is-expanded');
        var btns = examplesBox.querySelectorAll('.tool-example');
        for (var i = 0; i < btns.length; i++) {
          btns[i].classList.toggle('tool-example--extra', i >= exLimit && !expanded);
        }
        if (exMore) {
          var label = expanded ? '收起示例' : '更多示例 (' + (btns.length - exLimit) + ')';
          if (exMore.textContent !== label) { exMore.textContent = label; }
        }
      };
      var exMore = null;
      if (exList.length > exLimit) {
        exMore = el('button', 'tool-examples-more');
        exMore.type = 'button';
        exMore.addEventListener('click', function () {
          examplesBox.classList.toggle('is-expanded');
          syncExamples();
        });
        examplesBox.appendChild(exMore);
      }
      syncExamples();
      /* 说明行（第十八批）：点完示例显示「这套为什么这么列」+ 下一步引导——
         参考别人的填法时知道理由，改起来才有底气。role=status 让读屏也能感知 */
      exNote = el('p', 'pp-example-note');
      exNote.hidden = true;
      exNote.setAttribute('role', 'status');
      examplesBox.appendChild(exNote);
      examplesBox.addEventListener('click', function (ev) {
        var t = ev.target && ev.target.closest ? ev.target.closest('[data-ex]') : null;
        if (t) { applyExample(parseInt(t.getAttribute('data-ex'), 10)); }
      });
    }

    /* —— 行表格区（rowTable 组件；initial 按列序数组，rows() 返回 key 对象数组）——
       rowsList（第十三批）：多段行表，每段 {key, title?, cols, initial, minRows,
       maxRows, addText, removeText, ariaLabel}，列结构各不相同的行段（如寄养
       交接单的喂食/用品/注意）分表渲染、按各自 key 收集；cfg.rows（单段对象）
       向后兼容——内部统一转数组，单段文档行为不变。 */
    var rowCfgs = Array.isArray(cfg.rowsList) ? cfg.rowsList : (cfg.rows ? [cfg.rows] : []);
    var rts = [];
    if (rowsBox && rowCfgs.length) {
      rowCfgs.forEach(function (seg) {
        var segBadge = null;
        if (seg.title) {
          segBadge = badgeEl();
          rowSegs.push({
            badge: segBadge,
            rt: null,
            required: (seg.required && seg.required.length) ? seg.required
              : (seg.cols || []).map(function (c) { return c.key; })
          });
          rowsBox.appendChild(titleEl('pp-rows-title', seg.title, segBadge));
        }
        /* 每段独立子容器：UI.rowTable 以 data-rowtable-ready 做幂等守卫（同一 host
           只初始化一次），共用 rowsBox 会让第二段起全部静默返回 null */
        var segBox = el('div', 'pp-rows-seg');
        rowsBox.appendChild(segBox);
        var t = UI.rowTable(segBox, seg.cols, {
          initial: seg.initial,
          minRows: seg.minRows,
          maxRows: seg.maxRows,
          addText: seg.addText,
          removeText: seg.removeText,
          ariaLabel: seg.ariaLabel
        });
        if (t) {
          rts.push({ cfg: seg, rt: t });
          if (segBadge) { rowSegs[rowSegs.length - 1].rt = t; }
        }
      });
      if (rts.length) { rowsBox.removeAttribute('hidden'); }
    }

    /* 进度徽章刷新：字段组按非空字段数、行段按「必填列全非空」的行数。
       事件已现成（rowTable 输入/增删冒泡 input/click 到段容器、日历选值派发
       input），容器级委托即可，共享组件零改动；首刷铺开初始态（预填文档直接 ✓） */
    function refreshBadges() {
      fieldGroups.forEach(function (g) {
        var done = 0;
        g.items.forEach(function (inp) {
          if (String(inp.value == null ? '' : inp.value).trim() !== '') { done++; }
        });
        paintBadge(g.badge, done, g.items.length);
      });
      rowSegs.forEach(function (s) {
        if (!s.rt) { return; }
        var rowsArr = s.rt.rows();
        var done = 0;
        rowsArr.forEach(function (r) {
          var ok = true;
          for (var i = 0; i < s.required.length; i++) {
            if (String(r[s.required[i]] == null ? '' : r[s.required[i]]).trim() === '') { ok = false; break; }
          }
          if (ok) { done++; }
        });
        paintBadge(s.badge, done, rowsArr.length);
      });
    }
    if (fieldsBox) { fieldsBox.addEventListener('input', refreshBadges); }
    if (rowsBox) {
      rowsBox.addEventListener('input', refreshBadges);
      rowsBox.addEventListener('click', refreshBadges);
    }
    refreshBadges();

    /* —— 纸面实例：mount 时创建一次，generate 只 render（幂等，同内容零重建） —— */
    var paper = UI.paper(resultBox, { ariaLabel: (cfg.ui && cfg.ui.resultAria) || '' });

    /* 主按钮文案按文档具体化（cfg.ui.generateText，如「生成存钱表」；缺省保留模板文案） */
    var genTextEl = root.querySelector('[data-pp-generate-text]');
    if (genTextEl) {
      var wantLabel = (cfg.ui && cfg.ui.generateText) || '';
      if (wantLabel && genTextEl.textContent !== wantLabel) { genTextEl.textContent = wantLabel; }
    }

    function showError(msg) {
      errBox.textContent = msg;
      errBox.removeAttribute('hidden');
    }
    function clearError() {
      if (!errBox.hasAttribute('hidden')) {
        errBox.setAttribute('hidden', '');
        errBox.textContent = '';
      }
    }

    function collect() {
      var input = {};
      Object.keys(inputs).forEach(function (k) { input[k] = inputs[k].value; });
      rts.forEach(function (pair) { input[pair.cfg.key || 'tasks'] = pair.rt.rows(); });
      return input;
    }

    function generate(keepView) {
      clearError();
      var res;
      try { res = engine(collect(), cfg.labels || {}); }
      catch (e) { showError(engineErrMsg(e, cfg)); return; }
      if (!res || !res.blocks || !res.blocks.length) {
        showError(engineErrMsg({ message: 'EMPTY' }, cfg));
        return;
      }
      paper.render(res.blocks);
      /* 摘要占位符泛化：遍历引擎 disp 全键替换 {key}（split/join 规避 replace 的 $ 特殊序列）；
         disp 没有的占位符原样保留（便于排查 data js 写错键名） */
      var tpl = cfg.ui && cfg.ui.summaryTpl;
      if (tpl && sumBox) {
        var d = res.disp || {};
        var text = String(tpl);
        Object.keys(d).forEach(function (k) {
          text = text.split('{' + k + '}').join(d[k] == null ? '' : String(d[k]));
        });
        sumBox.textContent = text;
        sumBox.removeAttribute('hidden');
      }
      if (printBtn) { printBtn.removeAttribute('hidden'); }
      if (resetBtn) { resetBtn.removeAttribute('hidden'); }
      /* keepView（步进联动重算）不滚动：纸面原地更新即可，不抢视口 */
      if (!keepView && resultBox) {
        try { resultBox.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e2) { /* 旧浏览器忽略 */ }
      }
    }

    /* 整套套值（示例与重填共用同一口径，来回切换不漂移）：
       fieldsMap={字段名:值} 缺键=复位默认（date 无 value → 今天，不是清空）；
       rowsMap={段key: 二维数组} 缺键=复位 initial。值先比对再写（同状态零 DOM 写入） */
    function findField(name) {
      var list = cfg.fields || [];
      for (var i = 0; i < list.length; i++) {
        if (list[i].name === name) { return list[i]; }
      }
      return null;
    }
    function applyValues(fieldsMap, rowsMap) {
      var fmap = fieldsMap || {};
      Object.keys(inputs).forEach(function (k) {
        var v = Object.prototype.hasOwnProperty.call(fmap, k)
          ? String(fmap[k] == null ? '' : fmap[k])
          : fieldDefault(findField(k));
        if (inputs[k].value !== v) { inputs[k].value = v; }
      });
      var rmap = rowsMap || {};
      rts.forEach(function (pair) {
        var key = pair.cfg.key || 'tasks';
        var data = Object.prototype.hasOwnProperty.call(rmap, key) ? rmap[key] : pair.cfg.initial;
        pair.rt.setRows(data);
      });
      refreshBadges();
    }

    function reset() {
      clearError();
      hideExNote();
      applyValues(null, null);
      paper.clear();
      if (sumBox && !sumBox.hasAttribute('hidden')) {
        sumBox.setAttribute('hidden', '');
        sumBox.textContent = '';
      }
      if (printBtn) { printBtn.setAttribute('hidden', ''); }
      if (resetBtn) { resetBtn.setAttribute('hidden', ''); }
      try { root.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e2) { /* 忽略 */ }
    }

    /* 点示例 = 整套替换现成填法 → 直接生成纸面（一步看到成品；示例数据由
       data js 保证引擎合法，仍走 generate 的 try/catch 兜错误行） */
    function applyExample(idx) {
      var ex = exList[idx];
      if (!ex) { return; }
      clearError();
      applyValues(ex.fields || null, ex.rows || null);
      generate();
      showExNote(ex);
    }

    /* 说明行内容（第十八批）：有 note 讲「这套为什么这么列」，没有就只给下一步引导；
       同状态零 DOM 写入（textContent 先比对） */
    function showExNote(ex) {
      if (!exNote) { return; }
      var text = '已套用「' + (ex.label || '示例') + '」——' + (ex.note ? ex.note + '。' : '')
        + '改改标题和内容，就是你的版本。';
      if (exNote.textContent !== text) { exNote.textContent = text; }
      if (exNote.hidden) { exNote.hidden = false; }
    }
    function hideExNote() {
      if (exNote && !exNote.hidden) {
        exNote.hidden = true;
        exNote.textContent = '';
      }
    }

    /* 不能直接传 generate：click 会把事件对象当首参（keepView 恒真值 → 永不滚动） */
    genBtn.addEventListener('click', function () { generate(); });
    if (printBtn) {
      printBtn.addEventListener('click', function () {
        /* 打印页眉用文档标题（纸面 title block 优先，退回 SEO h1） */
        var t = inputs.title ? String(inputs.title.value || '') : '';
        paper.print({ title: t || (cfg.ui && cfg.ui.printTitle) || '' });
      });
    }
    if (resetBtn) { resetBtn.addEventListener('click', reset); }
  }

  function boot() {
    initTheme();
    initBackTop();
    var roots = doc.querySelectorAll('[data-pp-doc]');
    for (var i = 0; i < roots.length; i++) { mountDoc(roots[i]); }
  }

  /* boot 时机：defer 脚本按文档顺序执行——若本文件先于文档 data js 执行，
     PP_DOCS 还是空对象，同步 boot 会让 mountDoc 因 !cfg 静默 return（填写区永不渲染、零报错）。
     因此 deferred 场景下一律等 DOMContentLoaded（所有 defer 执行完才触发）；仅动态注入
     （readyState 已 complete）才立即跑。无 document（Node 冒烟 sandbox 执行本文件）→ 不 boot。 */
  if (!doc) {
    /* Node sandbox：到此为止 */
  } else if (doc.readyState === 'complete') {
    boot();
  } else {
    doc.addEventListener('DOMContentLoaded', boot);
  }
})(typeof window !== 'undefined' ? window : globalThis);
